// assets/delivery-date.js
// Calendario de fecha y franja horaria de entrega para la página de producto.
// Guarda la selección como propiedades del producto (line item properties),
// que Shopify copia a la orden.

const DAY_MS = 24 * 60 * 60 * 1000;

/** @param {Date} date UTC date @returns {string} YYYY-MM-DD */
const WEEKDAY_INDEX = { domingo: 0, lunes: 1, martes: 2, miercoles: 3, jueves: 4, viernes: 5, sabado: 6 };
const normalizeDay = (d) => d.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
const toIso = (date) => date.toISOString().slice(0, 10);

/** @param {string} iso @returns {Date} */
const fromIso = (iso) => new Date(`${iso}T00:00:00Z`);

const TIME_PATTERN = /(\d{1,2})(?:[:.](\d{2}))?\s*(a\.?\s?m\.?|p\.?\s?m\.?)?/gi;

/**
 * @param {string} hours
 * @param {string | undefined} minutes
 * @param {string | undefined} meridiem
 * @returns {number} minutos desde medianoche
 */
const toMinutes = (hours, minutes, meridiem) => {
  let h = Number(hours) % 12;
  if (!meridiem) h = Number(hours);
  else if (/p/i.test(meridiem)) h += 12;
  return h * 60 + Number(minutes || 0);
};

/**
 * Hora de inicio de una franja, en minutos desde medianoche.
 * Acepta "15:00 - 19:00", "3:00 pm - 7:00 pm" y "3:00 - 7:00 pm"
 * (si solo el final dice pm, el inicio también es de la tarde cuando cabe antes del final).
 * @param {string} value
 * @returns {number | null}
 */
const parseStartMinutes = (value) => {
  const times = [...value.matchAll(TIME_PATTERN)].filter((m) => m[2] !== undefined || m[3]);
  if (!times.length) return null;
  const [start, end] = times;
  let minutes = toMinutes(start[1], start[2], start[3]);
  if (!start[3] && end?.[3] && /p/i.test(end[3]) && Number(start[1]) < 12) {
    const endMinutes = toMinutes(end[1], end[2], end[3]);
    if (minutes + 12 * 60 <= endMinutes) minutes += 12 * 60;
  }
  return minutes;
};

/**
 * Fecha y hora actuales en la zona horaria de la tienda.
 * @param {string} timeZone
 */
const nowInTimeZone = (timeZone) => {
  let parts;
  try {
    parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());
  } catch {
    parts = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());
  }
  /** @type {Record<string, string>} */
  const p = {};
  for (const part of parts) p[part.type] = part.value;
  return {
    iso: `${p.year}-${p.month}-${p.day}`,
    minutes: Number(p.hour) * 60 + Number(p.minute),
    hour: Number(p.hour),
  };
};

class DeliveryDatePicker extends HTMLElement {
  /** @type {string | null} */
  #selectedDate = null;
  /** @type {string | null} */
  #selectedSlot = null;
  /** @type {Date} */
  #viewMonth = new Date();

  connectedCallback() {
    if (this.config) {
      this.#attachForm();
      return;
    }
    const configEl = this.querySelector('[data-delivery-config]');
    if (!configEl) return;

    this.config = JSON.parse(configEl.textContent || '{}');
    // Cada horario puede traer su hora de corte para el mismo día: "10:00 am - 3:00 pm | 11:00 am".
    // Y puede ser solo para ciertos días: "Sábado: 10:00 am - 4:00 pm | 12:00 pm". Si un día tiene
    // horarios propios, ese día se ofrecen solo esos; los demás días usan los horarios sin día.
    // Cada renglón se guarda por separado (no por su texto): el mismo horario puede estar
    // una vez para todos los días y otra para el sábado con distinta hora de corte.
    /** @type {{ label: string, days: Set<number> | null, cutoff: number | null }[]} */
    this.slotEntries = (this.config.timeSlots || [])
      .map((entry) => {
        let raw = entry;
        const dayPrefix = /^\s*([a-záéíóúñ ,y]+):\s*(.+)$/i.exec(entry);
        let days = null;
        if (dayPrefix) {
          days = new Set(dayPrefix[1].split(/,|\sy\s/).map((d) => WEEKDAY_INDEX[normalizeDay(d)]).filter((d) => d !== undefined));
          if (days.size) raw = dayPrefix[2]; else days = null;
        }
        const [label = '', cutoff] = raw.split('|').map((part) => part.trim());
        return { label, days, cutoff: cutoff ? parseStartMinutes(cutoff) : null };
      })
      .filter((slot) => slot.label);
    this.config.timeSlots = this.slotEntries.map((slot) => slot.label);
    this.config.blockedDates = new Set((this.config.blockedDates || []).map((s) => s.trim()).filter(Boolean));
    this.config.disabledWeekdays = new Set(this.config.disabledWeekdays || []);

    this.els = {
      grid: this.querySelector('[data-grid]'),
      weekdays: this.querySelector('[data-weekdays]'),
      monthLabel: this.querySelector('[data-month-label]'),
      prev: this.querySelector('[data-prev]'),
      next: this.querySelector('[data-next]'),
      toggle: this.querySelector('[data-toggle]'),
      heading: this.querySelector('[data-heading]'),
      popover: this.querySelector('[data-popover]'),
      fieldText: this.querySelector('[data-field-text]'),
      slotsWrapper: this.querySelector('[data-slots-wrapper]'),
      select: this.querySelector('[data-slot-select]'),
      error: this.querySelector('[data-error]'),
      inputDate: this.querySelector('[data-input-date]'),
      inputTime: this.querySelector('[data-input-time]'),
      inputIso: this.querySelector('[data-input-iso]'),
    };

    this.#computeRange();
    const first = fromIso(this.firstDate);
    this.#viewMonth = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth(), 1));

    this.#renderWeekdays();
    this.#renderCalendar();

    this.els.prev.addEventListener('click', () => this.#shiftMonth(-1));
    this.els.next.addEventListener('click', () => this.#shiftMonth(1));
    this.els.toggle.addEventListener('click', () => (this.#isOpen() ? this.#close() : this.#open()));
    this.els.select.addEventListener('change', () => {
      this.#selectedSlot = this.els.select.value || null;
      this.els.select.closest('.delivery-date__select-wrap')?.classList.remove('is-invalid');
      this.#updateTimeHint();
      this.#sync();
    });
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.#isOpen()) {
        this.#close();
        this.els.toggle.focus();
      }
    });

    // "Hora de envío" se muestra recién cuando hay una fecha elegida.
    this.els.slotsWrapper.hidden = true;

    this.#attachForm();
  }

  disconnectedCallback() {
    window.removeEventListener('submit', this.#onSubmit, true);
    window.removeEventListener('click', this.#onAddClick, true);
    document.removeEventListener('click', this.#onOutsideClick);
  }

  #isOpen() {
    return !this.els.popover.hidden;
  }

  #open() {
    if (this.#selectedDate) {
      const selected = fromIso(this.#selectedDate);
      this.#viewMonth = new Date(Date.UTC(selected.getUTCFullYear(), selected.getUTCMonth(), 1));
    }
    this.#renderCalendar();
    this.els.popover.hidden = false;
    this.els.toggle.setAttribute('aria-expanded', 'true');
    document.addEventListener('click', this.#onOutsideClick);
    const focusTarget =
      this.els.grid.querySelector('[aria-pressed="true"]') || this.els.grid.querySelector('button:not(:disabled)');
    /** @type {HTMLElement | null} */ (focusTarget)?.focus({ preventScroll: true });
  }

  #close() {
    this.els.popover.hidden = true;
    this.els.toggle.setAttribute('aria-expanded', 'false');
    document.removeEventListener('click', this.#onOutsideClick);
  }

  /** @param {MouseEvent} event */
  #onOutsideClick = (event) => {
    const target = /** @type {Node} */ (event.target);
    // El calendario se vuelve a dibujar al cambiar de mes, así que el botón
    // pulsado puede ya no estar en el DOM: se ignora ese caso.
    if (!target.isConnected || this.contains(target)) return;
    this.#close();
  };

  #computeRange() {
    const { timezone, minDays, cutoffHour, maxDays } = this.config;
    const now = nowInTimeZone(timezone);
    this.now = now;
    let lead = Number(minDays) || 0;
    if (Number(cutoffHour) < 24 && now.hour >= Number(cutoffHour)) lead += 1;

    const today = fromIso(now.iso);
    this.firstDate = toIso(new Date(today.getTime() + lead * DAY_MS));
    this.lastDate = toIso(new Date(today.getTime() + Math.max(lead, Number(maxDays) || 60) * DAY_MS));
  }

  /** @param {string} iso */
  #isAvailable(iso) {
    if (iso < this.firstDate || iso > this.lastDate) return false;
    if (this.config.blockedDates.has(iso)) return false;
    if (this.config.disabledWeekdays.has(fromIso(iso).getUTCDay())) return false;
    if (this.config.timeSlots.length && this.#availableSlots(iso).length === 0) return false;
    return true;
  }

  /** Horarios que se ofrecen ese día de la semana. @param {string} iso @returns {string[]} */
  #slotsForDay(iso) {
    return this.#slotEntriesForDay(iso).map((slot) => slot.label);
  }

  /** @param {string} iso */
  #slotEntriesForDay(iso) {
    const general = this.slotEntries.filter((slot) => !slot.days);
    if (!iso) return general;
    const weekday = new Date(`${iso}T12:00:00Z`).getUTCDay();
    const own = this.slotEntries.filter((slot) => slot.days?.has(weekday));
    return own.length ? own : general;
  }

  /** @param {string} iso @returns {string[]} */
  #availableSlots(iso) {
    const slots = this.#slotEntriesForDay(iso);
    if (iso !== this.now.iso) return slots.map((slot) => slot.label);
    const bufferMinutes = (Number(this.config.sameDayBufferHours) || 0) * 60;
    return slots
      .filter((slot) => {
        if (slot.cutoff !== null) return this.now.minutes < slot.cutoff;
        const start = parseStartMinutes(slot.label);
        return start === null || start >= this.now.minutes + bufferMinutes;
      })
      .map((slot) => slot.label);
  }

  #renderWeekdays() {
    const formatter = new Intl.DateTimeFormat(this.config.locale, { weekday: 'short', timeZone: 'UTC' });
    // 2023-01-02 fue lunes: la semana empieza en lunes.
    const labels = [];
    for (let i = 0; i < 7; i++) {
      labels.push(`<span>${formatter.format(new Date(Date.UTC(2023, 0, 2 + i))).replace('.', '')}</span>`);
    }
    this.els.weekdays.innerHTML = labels.join('');
  }

  #renderCalendar() {
    const { locale } = this.config;
    const year = this.#viewMonth.getUTCFullYear();
    const month = this.#viewMonth.getUTCMonth();

    const monthText = new Intl.DateTimeFormat(locale, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(this.#viewMonth);
    this.els.monthLabel.textContent = monthText.charAt(0).toUpperCase() + monthText.slice(1);

    const firstOfMonth = new Date(Date.UTC(year, month, 1));
    const daysInMonth = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
    const offset = (firstOfMonth.getUTCDay() + 6) % 7; // lunes = 0

    const longFormatter = new Intl.DateTimeFormat(locale, { dateStyle: 'full', timeZone: 'UTC' });
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < offset; i++) fragment.append(document.createElement('span'));

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(Date.UTC(year, month, day));
      const iso = toIso(date);
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'delivery-date__day';
      button.textContent = String(day);
      button.dataset.date = iso;
      button.setAttribute('aria-label', longFormatter.format(date));
      button.setAttribute('aria-pressed', String(iso === this.#selectedDate));
      button.disabled = !this.#isAvailable(iso);
      button.addEventListener('click', () => this.#selectDate(iso));
      fragment.append(button);
    }

    this.els.grid.replaceChildren(fragment);

    const firstMonth = this.firstDate.slice(0, 7);
    const lastMonth = this.lastDate.slice(0, 7);
    const current = toIso(this.#viewMonth).slice(0, 7);
    this.els.prev.disabled = current <= firstMonth;
    this.els.next.disabled = current >= lastMonth;
  }

  /** @param {number} delta */
  #shiftMonth(delta) {
    this.#viewMonth = new Date(
      Date.UTC(this.#viewMonth.getUTCFullYear(), this.#viewMonth.getUTCMonth() + delta, 1)
    );
    this.#renderCalendar();
  }

  /** @param {string} iso */
  #selectDate(iso) {
    this.#selectedDate = iso;
    const slots = this.#availableSlots(iso);
    if (this.#selectedSlot && !slots.includes(this.#selectedSlot)) this.#selectedSlot = null;

    this.#renderCalendar();
    this.#renderSlots(slots);
    this.#sync();
    this.#close();
    this.els.toggle.focus({ preventScroll: true });
  }

  /** @param {string[]} slots */
  #renderSlots(slots) {
    const { select } = this.els;
    if (!this.config.timeSlots.length) return;

    const placeholder = document.createElement('option');
    placeholder.value = '';
    placeholder.textContent = 'Selecciona un horario';
    const options = [placeholder];

    for (const slot of this.#slotsForDay(this.#selectedDate)) {
      const option = document.createElement('option');
      option.value = slot;
      option.textContent = slot;
      option.disabled = !slots.includes(slot);
      option.selected = slot === this.#selectedSlot;
      options.push(option);
    }
    select.replaceChildren(...options);
    select.disabled = false;
    this.els.slotsWrapper.hidden = false;
    this.#updateTimeHint();
  }

  /** @param {'full' | 'long'} style */
  #formattedDate(style) {
    if (!this.#selectedDate) return '';
    const text = new Intl.DateTimeFormat(this.config.locale, { dateStyle: style, timeZone: 'UTC' }).format(
      fromIso(this.#selectedDate)
    );
    return text.charAt(0).toUpperCase() + text.slice(1);
  }

  #sync() {
    const { inputDate, inputTime, inputIso, fieldText } = this.els;
    const dateText = this.#formattedDate('full');

    inputDate.value = dateText;
    // Dato interno para el panel del florista, en formato día-mes-año (01-10-2026).
    inputIso.value = this.#selectedDate ? this.#selectedDate.split('-').reverse().join('-') : '';
    inputTime.value = this.#selectedSlot || '';
    inputDate.disabled = inputIso.disabled = !this.#selectedDate;
    inputTime.disabled = !this.#selectedSlot;

    if (this.#selectedDate) {
      fieldText.textContent = this.#formattedDate('long');
      fieldText.classList.remove('is-placeholder');
      // Ya eligió la fecha: el título deja de vibrar.
      this.els.heading?.classList.remove('delivery-date__label--attention', 'delivery-date__label--nudge');
    }

    if (this.#validationMessage() === null) this.#showError(null);
  }

  /** @returns {string | null} */
  #validationMessage() {
    const { messages, timeSlots } = this.config;
    if (this.config.paused) return messages.paused;
    if (!this.config.required) return null;
    if (!this.#selectedDate) return messages.missingDate;
    if (timeSlots.length && !this.#selectedSlot) return messages.missingTime;
    return null;
  }

  /** Muestra "Ahora elige el horario" mientras falte elegirlo. */
  #updateTimeHint() {
    const hint = this.querySelector('[data-time-hint]');
    const wrap = this.els.select?.closest('.delivery-date__select-wrap');
    const pending = Boolean(this.#selectedDate) && !this.#selectedSlot && this.config.timeSlots.length > 0;
    if (hint) hint.hidden = !pending;
    wrap?.classList.toggle('is-pending', pending);
  }

  /** @param {string | null} message */
  #showError(message) {
    this.els.error.hidden = !message;
    this.els.error.textContent = message || '';
  }

  #attachForm() {
    // Horizon atiende el envío en la fase de captura de `document`, así que
    // escuchamos en `window` (captura), que recibe el evento antes, y podemos frenarlo.
    window.addEventListener('submit', this.#onSubmit, true);
    window.addEventListener('click', this.#onAddClick, true);
  }

  /**
   * Revisa que haya fecha y horario; si falta algo muestra el aviso.
   * La usa también el botón "Comprar ahora".
   * @returns {boolean}
   */
  validate() {
    const message = this.#validationMessage();
    if (!message) return true;
    this.#showError(message);
    if (!this.#selectedDate) {
      this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      this.#nudgeHeading();
    } else {
      // Falta el horario: llevamos al cliente a la lista y la marcamos.
      const wrap = this.els.select?.closest('.delivery-date__select-wrap');
      (wrap || this).scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (wrap) {
        wrap.classList.remove('is-invalid');
        void wrap.offsetWidth;
        wrap.classList.add('is-invalid');
      }
    }
    return false;
  }

  /**
   * Click en "Agregar al carrito" (o en la barra fija de abajo): si falta fecha u horario,
   * lo frenamos antes que el tema, así no hace la animación de "agregado".
   * @param {MouseEvent} event
   */
  #onAddClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const mainButton = target.closest?.('button[name="add"]');
    const stickyButton = target.closest?.('sticky-add-to-cart button, .sticky-add-to-cart button');
    if (!mainButton && !stickyButton) return;
    if (mainButton && /** @type {HTMLButtonElement} */ (mainButton).form?.getAttribute('id') !== this.config.formId) return;
    if (this.#validationMessage() === null) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.validate();
  };

  /** Sacudida fuerte del título cuando intentan comprar sin fecha. */
  #nudgeHeading() {
    const heading = this.els.heading;
    if (!heading) return;
    heading.classList.remove('delivery-date__label--nudge');
    void heading.offsetWidth; // reinicia la animación
    heading.classList.add('delivery-date__label--nudge');
    // Al terminar, vuelve la vibración suave de siempre.
    heading.addEventListener('animationend', () => heading.classList.remove('delivery-date__label--nudge'), { once: true });
  }

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    // getAttribute: `form.id` devolvería el <input name="id"> de la variante.
    if (form?.getAttribute?.('id') !== this.config.formId) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (!this.validate()) return;

    // Si hay complementos marcados, el bloque de complementos agrega todo junto.
    const addons = /** @type {any} */ (document.querySelector('product-addons'));
    if (addons?.hasSelection?.()) {
      addons.addWithProduct(form, /** @type {HTMLButtonElement | null} */ (event.submitter));
      return;
    }
    this.#addAndGoToCart(form, /** @type {HTMLButtonElement | null} */ (event.submitter));
  };

  /** Agrega el producto (con fecha y horario) y lleva a la página del carrito. */
  async #addAndGoToCart(form, submitter) {
    submitter?.setAttribute('aria-busy', 'true');
    if (submitter) submitter.disabled = true;
    try {
      const routes = /** @type {any} */ (window).Shopify?.routes?.root || '/';
      const response = await fetch(`${routes}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || 'No se pudo agregar al carrito.');
      }
      window.location.href = `${routes}cart`;
    } catch (error) {
      this.#showError(error instanceof Error ? error.message : String(error));
      submitter?.removeAttribute('aria-busy');
      if (submitter) submitter.disabled = false;
    }
  }
}

if (!customElements.get('delivery-date-picker')) {
  customElements.define('delivery-date-picker', DeliveryDatePicker);
}
